import React, { useCallback, useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { Dropdown, Input, SalvButton, SalvModal, Size } from '@salvapp/ui-components';
import { Dayjs } from 'dayjs';
import './FinalRiskOverrideModal.scss';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { Link } from 'react-router';
import { InternalApiAddFinalRiskOverride, InternalApiFinalRiskLevel } from '../api-internal';
import { formatEnumLabel } from '../common/helper';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { fetchFinalRiskLevels, fetchRiskOverrideReasons, useAddFinalRiskOverride } from './riskActions';

const validationSchema = Yup.object().shape({
  riskLevel: Yup.string().required(),
  reason: Yup.string().required(),
  endDate: Yup.mixed<Dayjs>().optional(),
  comment: Yup.string().required('Required'),
});

export interface RiskOverrideFormValues {
  riskLevel: InternalApiFinalRiskLevel;
  reason: string | undefined;
  endDate?: Dayjs;
  comment: string;
}

export interface FinalRiskOverrideModalProps {
  personId: string;
}

const FinalRiskOverrideModal = ({ personId }: FinalRiskOverrideModalProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const riskOverrideReasonsPage = useSelector((state: AppState) => state.risk.overrideReasons, shallowEqual);
  const dispatch = useDispatch();
  const { mutateAsync: addOverride } = useAddFinalRiskOverride(personId);
  const riskLevelOptions = Object.values(InternalApiFinalRiskLevel).map((v) => ({
    key: v,
    label: formatEnumLabel(v),
    value: v,
  }));

  const initialValues: RiskOverrideFormValues = {
    riskLevel: InternalApiFinalRiskLevel.LOW,
    reason: undefined,
    comment: '',
  };

  const initialFetch = useCallback(async () => {
    await dispatch(fetchRiskOverrideReasons(0, 50));
    await dispatch(fetchFinalRiskLevels());
  }, [dispatch]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  const riskOverrideReasonOptions = () => {
    return riskOverrideReasonsPage.content.map((reason) => ({
      key: reason.handle,
      value: reason.handle,
      label: reason.reason,
    }));
  };
  const submit = async (values: RiskOverrideFormValues, { setSubmitting }: FormikHelpers<RiskOverrideFormValues>) => {
    if (!values.reason) {
      return;
    }
    setSubmitting(true);
    const riskOverride: InternalApiAddFinalRiskOverride = {
      reasonHandle: values.reason,
      level: values.riskLevel,
      endTime: values.endDate?.toDate(),
      comment: values.comment,
    };
    await addOverride(riskOverride);
    setSubmitting(false);
    setShowModal(false);
  };

  return (
    <>
      <div>
        <SalvButton
          variant='secondary'
          size={Size.SMALL}
          onClick={() => setShowModal(true)}
          label={'Override'}
          icon={faPenToSquare}
        ></SalvButton>
      </div>
      <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
        {({ values, isSubmitting, setFieldValue, isValid }: FormikProps<RiskOverrideFormValues>) => (
          <SalvModal show={showModal} onHide={() => setShowModal(false)}>
            <Form>
              <SalvModal.Header closeButton>Override risk level</SalvModal.Header>
              <SalvModal.Body>
                <div className='row m-b-24'>
                  <div className='col'>
                    <Dropdown
                      options={riskLevelOptions}
                      onChange={(opt: DropdownOption[]) => setFieldValue('riskLevel', opt[0].value)}
                      placeholder='Select one...'
                      label='Risk level'
                    />
                  </div>
                  <div className='col'>
                    <div className='d-flex justify-content-between m-b-8'>
                      <span className='input-label'>Reason</span>
                      <Link className='text-decoration-underline' to='/risk/override-reasons'>
                        Manage
                      </Link>
                    </div>
                    <Dropdown
                      options={riskOverrideReasonOptions()}
                      onChange={(opt: DropdownOption[]) => setFieldValue('reason', opt[0].value)}
                      placeholder='Select one...'
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col'>
                    <Input
                      label='Comment'
                      placeholder='Enter here'
                      maxLength={250}
                      value={values.comment}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('comment', e.target.value)}
                    />
                  </div>
                </div>
              </SalvModal.Body>
              <SalvModal.Footer>
                <SalvButton variant='muted' onClick={() => setShowModal(false)} label='Close' />
                <SalvButton disabled={isSubmitting || !isValid} type='submit' variant='primary' label='Save' />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        )}
      </Formik>
    </>
  );
};

export default FinalRiskOverrideModal;
